import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd'
import useTopicActions from './useTopicActions'
import { useEffect } from 'react'
import TopicRow from './TopicRow'
import TopicCard from './TopicCard'
import TopicSolvedRow from './TopicSolvedRow'
import type { Topic, TopicFilters } from '../../types'
import type { TopicView } from './ViewToggle'

interface Props {
  topics: Topic[]
  teamId: number
  activeTab: string
  canReorder: boolean
  filters?: TopicFilters
  onRowsChange?: (rows: Topic[]) => void
  onManual?: () => void
  view?: TopicView
  onEdit: (topic: Topic) => void
  onResolve: (topic: Topic) => void
  onConvert: (topic: Topic) => void
}

export default function TopicList({
  topics, teamId, activeTab, canReorder, filters, view = 'list',
  onEdit, onResolve, onConvert, onRowsChange, onManual
}: Props) {
  const actions = useTopicActions(topics, `/teams/${teamId}/topics`, filters)
  const orderedTopics = actions.rows
  useEffect(() => { onRowsChange?.(orderedTopics) }, [orderedTopics, onRowsChange])
  const moveEdge = (topic: Topic, edge: 'top' | 'bottom') => { onManual?.(); actions.edge(topic, edge) }

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result
    if (!canReorder || !destination || (destination.index === source.index && destination.droppableId === source.droppableId)) return
    const moved = orderedTopics.find(t => String(t.id) === result.draggableId)
    if (!moved) return
    const type = destination.droppableId as Topic['topic_type']
    const rest = orderedTopics.filter(t => t.id !== moved.id)
    const target = rest.filter(t => t.topic_type === type)
    target.splice(destination.index, 0, { ...moved, topic_type: type })
    const other = rest.filter(t => t.topic_type !== type)
    onManual?.()
    actions.reorder(type === 'short_term' ? [...target, ...other] : [...other, ...target], { topic_id: moved.id, destination_type: type })
  }

  if (activeTab === 'solved') {
    const grouped: Record<string, Topic[]> = {}
    topics.forEach(topic => {
      const date = topic.resolved_at
        ? new Date(topic.resolved_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        : 'Unknown Date'
      if (!grouped[date]) grouped[date] = []
      grouped[date].push(topic)
    })

    return (
      <div className="space-y-6">
        {Object.entries(grouped).map(([date, groupTopics]) => (
          <div key={date}>
            <h3 className="font-bold uppercase tracking-widest text-text-secondary mb-2" style={{ fontSize: '12px' }}>{date}</h3>
            <div className={view === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3' : 'space-y-2'}>
              {groupTopics.map(topic => (
                <TopicSolvedRow key={topic.id} topic={topic} teamId={teamId} />
              ))}
            </div>
          </div>
        ))}
      </div>
    )
  }

  // Grid view renders cards; drag reordering stays list-only.
  if (view === 'grid') {
    return (
      <>
      {actions.error && <p role="alert" className="text-pinnacle-red mb-3">{actions.error}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {orderedTopics.map(topic => (
          <TopicCard
            key={topic.id}
            topic={topic}
            teamId={teamId}
            onPriority={actions.priority}
            onEdge={canReorder ? moveEdge : undefined}
            onEdit={onEdit}
            onResolve={onResolve}
            onConvert={onConvert}
          />
        ))}
      </div>
      </>
    )
  }

  return (
    <>
    {actions.error && <p role="alert" className="text-pinnacle-red mb-3">{actions.error}</p>}
    <DragDropContext onDragEnd={handleDragEnd}>
      {(['short_term', 'long_term'] as const).filter(type => !filters?.topic_type || filters.topic_type === type).map(type => (
      <section key={type} className="mb-6">
      <h2 className="text-sm font-bold uppercase mb-3">{type === 'short_term' ? 'Short Term' : 'Camp'} ({orderedTopics.filter(t => t.topic_type === type).length})</h2>
      <Droppable droppableId={type} isDropDisabled={!canReorder}>
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2 min-h-16">
            {orderedTopics.filter(t => t.topic_type === type).length === 0 && <p className="text-sm text-text-secondary p-3">No topics in this section.</p>}
            {orderedTopics.filter(t => t.topic_type === type).map((topic, index) => (
              <Draggable key={topic.id} draggableId={String(topic.id)} index={index} isDragDisabled={!canReorder}>
                {(prov, snapshot) => (
                  <div
                    ref={prov.innerRef}
                    {...prov.draggableProps}
                    style={prov.draggableProps.style}
                    className={snapshot.isDragging ? 'shadow-lg rounded-lg bg-white' : ''}
                  >
                    <TopicRow
                      topic={topic}
                      index={index}
                      teamId={teamId}
                      canDrag={canReorder}
                      onPriority={actions.priority}
                      onEdge={canReorder ? moveEdge : undefined}
                      onEdit={onEdit}
                      onResolve={onResolve}
                      onConvert={onConvert}
                      dragHandleProps={prov.dragHandleProps}
                    />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
      </section>
      ))}
    </DragDropContext>
    </>
  )
}
